// src/features/alerts/AlertsList.jsx - OPTIMIZADO: Sin dobles consultas
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Badge, Spinner, Tabs, Tab } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUserFriends, FaBriefcase, FaClock } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import TableWrapper from '../../components/TableWrapper';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';
import FilterSelect from '../../components/FilterSelect';
import AlertsService from './AlertsService';
import { compareExpirationDates, getAlertStyle } from './utils/alertsUtils';
import { getTipoDescripcion, TIPOS_INSPECCIONES, FORMATO_FECHA } from '../../utils/constants';
import './Alerts.css';

// ✅ CORREGIDO: Extender dayjs con plugin UTC
dayjs.extend(utc);

/**
 * Componente principal que muestra las alertas de vencimientos
 */
function AlertsList() {
  // Estados
  const [loading, setLoading] = useState(true);
  const [documentsExpirations, setDocumentsExpirations] = useState([]);
  const [inspectionsExpirations, setInspectionsExpirations] = useState([]);
  const [documentsFilters, setDocumentsFilters] = useState([]);
  const [inspectionsFilters, setInspectionsFilters] = useState([]);
  const [activeTab, setActiveTab] = useState('documents');
  const [activeTimeFilters, setActiveTimeFilters] = useState([]);
  const [activeDocumentFilters, setActiveDocumentFilters] = useState([]);
  const [activeInspectionFilters, setActiveInspectionFilters] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [docsGridApi, setDocsGridApi] = useState(null);
  const [inspGridApi, setInspGridApi] = useState(null);

  // ✅ CONSTANTES ESTÁTICAS para evitar re-creación en cada render
  const timeFilterOptions = useMemo(() => [
    { id: 'warning', label: 'MENOS DE 60 DÍAS', value: 'warning', checked: false },
    { id: 'critical', label: 'MENOS DE 30 DÍAS', value: 'critical', checked: false },
    { id: 'expired', label: 'VENCIDOS', value: 'expired', checked: false },
    { id: 'ok_6_month', label: 'HASTA 6 MESES', value: 'ok_6_month', checked: false },
    { id: 'ok_12_month', label: 'HASTA 12 MESES', value: 'ok_12_month', checked: false },
    { id: 'ok_18_month', label: 'HASTA 18 MESES', value: 'ok_18_month', checked: false }
  ], []);

  // ✅ FUNCIÓN HELPER para formatear fechas correctamente (memoizada)
  const formatDateSafely = useCallback((dateValue) => {
    if (!dateValue) return '';
    
    try {
      // Si la fecha viene con timezone UTC, usarla en modo UTC para evitar conversión
      if (typeof dateValue === 'string' && dateValue.includes('T') && dateValue.includes('Z')) {
        return dayjs.utc(dateValue).format(FORMATO_FECHA);
      }
      // Si solo es fecha (YYYY-MM-DD), usar modo normal
      return dayjs(dateValue).format(FORMATO_FECHA);
    } catch (error) {
      console.error('Error al formatear fecha:', dateValue, error);
      return '';
    }
  }, []);

  // ✅ FUNCIÓN para parsear fechas de manera segura (memoizada)
  const parseDateSafely = useCallback((dateValue) => {
    if (!dateValue) return null;
    
    try {
      // Si la fecha viene con timezone UTC, parsearla en modo UTC
      if (typeof dateValue === 'string' && dateValue.includes('T') && dateValue.includes('Z')) {
        return dayjs.utc(dateValue);
      }
      // Si solo es fecha (YYYY-MM-DD), usar modo normal
      return dayjs(dateValue);
    } catch (error) {
      console.error('Error al parsear fecha:', dateValue, error);
      return null;
    }
  }, []);

  // ✅ FUNCIÓN PARA NORMALIZAR TEXTO REMOVIENDO ACENTOS/TILDES (memoizada)
  const normalizeText = useCallback((text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase()
      .trim();
  }, []);

  // ✅ FUNCIÓN para verificar si un vencimiento tiene más de 1 año vencido (memoizada)
  const isMoreThanOneYearExpired = useCallback((expirationDate) => {
    if (!expirationDate) return false;
    
    const oneYearAgo = dayjs().subtract(1, 'year').startOf('day');
    const expDate = parseDateSafely(expirationDate);
    
    if (!expDate) return false;
    
    // Retorna true si la fecha de vencimiento es anterior a hace 1 año
    return expDate.startOf('day').isBefore(oneYearAgo);
  }, [parseDateSafely]);

  // ✅ CALCULAR tipo de alerta basado en la fecha usando UTC (memoizada)
  const calculateAlertType = useCallback((date) => {
    if (!date) return '';
    
    const today = dayjs().startOf('day');
    const targetDate = parseDateSafely(date);
    
    if (!targetDate) return '';
    
    const timeDifference = targetDate.startOf('day').diff(today, 'day');
    
    if (timeDifference < 0) {
      return 'expired';
    } else if (timeDifference <= 30) {
      return 'critical';
    } else if (timeDifference <= 60) {
      return 'warning';
    } else if (timeDifference <= 180) {
      return 'ok_6_month';
    } else if (timeDifference <= 365) {
      return 'ok_12_month';
    } else {
      return 'ok_18_month'; // 18 meses o más
    }
  }, [parseDateSafely]);

  // ✅ FORMATEAR datos de documentos (memoizada)
  const formatDocumentsData = useCallback((documents) => {
    return documents
      .filter(doc => {
        // ✅ FILTRO ADICIONAL: Verificar que tiene una relación válida
        const hasValidPerson = doc.person && 
          (doc.person.id || doc.person.firstName || doc.person.businessName);
        const hasValidWork = doc.work && 
          (doc.work.id || doc.work.name);
        
        return hasValidPerson || hasValidWork;
      })
      .map(doc => {
        const person = doc.person || null;
        const work = doc.work || null;
        
        // Calcular tipo de alerta
        const alertType = calculateAlertType(doc.endDate);
        
        return {
          ...doc,
          alertType,
          displayName1: person ? (person.businessName || person.firstName || '') : (work?.name || ''),
          displayName2: person && !person.businessName ? person.surname || '' : '',
          document: person && !person.businessName ? person.document || '' : ''
        };
      });
  }, [calculateAlertType]);

  // ✅ FORMATEAR datos de inspecciones (memoizada)
  const formatInspectionsData = useCallback((inspections) => {
    return inspections
      .filter(inspection => {
        // ✅ FILTRO OBLIGATORIO: Debe tener assignedTool válido
        const hasValidAssignedTool = inspection.assignedTool && 
          inspection.assignedTool !== null && 
          inspection.assignedTool.id && 
          inspection.assignedTool.tool && 
          inspection.assignedTool.tool.name;

        
        return hasValidAssignedTool;
      })
      .map(inspection => {
        // assignedTool ya está validado que existe
        const assignedTool = inspection.assignedTool;
        
        // Determinar la persona (priority: inspection.person > assignedTool.person > responsable)
        const person = inspection.person || 
                       assignedTool.person || 
                       (inspection.responsable ? { firstName: inspection.responsable, surname: "" } : null);
        
        // Calcular tipo de alerta
        const alertType = calculateAlertType(inspection.date);
        
        return {
          ...inspection,
          alertType,
          assignedTool,
          displayName1: person ? (person.firstName || '') : 'Sin persona asignada',
          displayName2: person ? (person.surname || '') : '',
          document: person ? (person.document || '') : '',
          equipment: assignedTool.tool.name, // Ya validamos que existe
          typeDescription: getTipoDescripcion(inspection.type, TIPOS_INSPECCIONES)
        };
      });
  }, [calculateAlertType]);

  // ✅ OBTENER filtros de tipos de documentos (memoizada)
  const getDocumentTypeFilters = useCallback((documents) => {
    const typeFilters = [];
    const uniqueTypes = new Set();
    
    documents.forEach(doc => {
      if (doc.name && !uniqueTypes.has(doc.name)) {
        uniqueTypes.add(doc.name);
        typeFilters.push({
          id: doc.name,
          label: doc.name,
          value: doc.name,
          checked: false
        });
      }
    });
    
    // Ordenar alfabéticamente
    typeFilters.sort((a, b) => a.label.localeCompare(b.label));
    return typeFilters;
  }, []);

  // ✅ OBTENER filtros de tipos de inspecciones (memoizada)
  const getInspectionTypeFilters = useCallback((inspections) => {
    const typeFilters = [];
    const uniqueTypes = new Set();
    
    inspections.forEach(inspection => {
      if (inspection.type && !uniqueTypes.has(inspection.type)) {
        uniqueTypes.add(inspection.type);
        typeFilters.push({
          id: inspection.type,
          label: getTipoDescripcion(inspection.type, TIPOS_INSPECCIONES),
          value: inspection.type,
          checked: false
        });
      }
    });
    
    // Ordenar alfabéticamente
    typeFilters.sort((a, b) => a.label.localeCompare(b.label));
    return typeFilters;
  }, []);

  // ✅ CARGAR datos de vencimientos - SOLO UNA VEZ (memoizada)
  const loadExpirations = useCallback(async () => {
    try {
      setLoading(true);
      
      // ✅ SINGLE REQUEST - AlertsService ya filtra los registros nulos
      const expirations = await AlertsService.getAllExpirations();
      
      // Formatear los datos para las tablas
      const formattedDocuments = formatDocumentsData(expirations.documents);
      const formattedInspections = formatInspectionsData(expirations.inspections);
      
      // Actualizar estados EN BATCH para evitar múltiples renders
      setDocumentsExpirations(formattedDocuments);
      setInspectionsExpirations(formattedInspections);
      setDocumentsFilters(getDocumentTypeFilters(formattedDocuments));
      setInspectionsFilters(getInspectionTypeFilters(formattedInspections));
      
    } catch (error) {
      console.error('Error al cargar vencimientos:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de vencimientos'
      });
    } finally {
      setLoading(false);
    }
  }, [formatDocumentsData, formatInspectionsData, getDocumentTypeFilters, getInspectionTypeFilters]);

  // ✅ CARGAR datos al iniciar - SOLO UNA VEZ con array de dependencias vacío
  useEffect(() => {
    loadExpirations();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Array vacío = solo se ejecuta una vez al montar

  // ✅ CONFIGURACIÓN para filtros de texto SIN ACENTOS (memoizada)
  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true,
    // ✅ Función personalizada para comparar texto ignorando acentos
    textMatcher: ({ filterOption, value, filterText }) => {
      if (!filterText) return true;
      
      const normalizedValue = normalizeText(value);
      const normalizedFilter = normalizeText(filterText);
      
      switch (filterOption) {
        case 'contains':
          return normalizedValue.includes(normalizedFilter);
        case 'notContains':
          return !normalizedValue.includes(normalizedFilter);
        case 'equals':
          return normalizedValue === normalizedFilter;
        case 'notEqual':
          return normalizedValue !== normalizedFilter;
        case 'startsWith':
          return normalizedValue.startsWith(normalizedFilter);
        case 'endsWith':
          return normalizedValue.endsWith(normalizedFilter);
        default:
          return normalizedValue.includes(normalizedFilter);
      }
    }
  }), [normalizeText]);

  // ✅ CONFIGURACIÓN para campos de fecha (memoizada)
  const dateFilterParams = useMemo(() => ({
    debounceMs: 200
  }), []);

  // Handlers para cambios en filtros (memoizados para evitar re-renders)
  const handleDocumentFiltersChange = useCallback((filters) => {
    setActiveDocumentFilters(filters);
  }, []);

  const handleInspectionFiltersChange = useCallback((filters) => {
    setActiveInspectionFilters(filters);
  }, []);

  const handleTimeFiltersChange = useCallback((filters) => {
    setActiveTimeFilters(filters);
  }, []);

  // Determinar si un elemento debe estar oculto según los filtros activos
  const isItemFiltered = useCallback((item, activeFilters) => {
    if (activeFilters.length === 0) {
      return true; // Mostrar todo cuando no hay filtros
    }
    
    // Verificar si algún filtro activo coincide con el elemento
    const match = activeFilters.some(filter => 
      filter.value === item.alertType || 
      filter.value === item.type || 
      filter.value === item.name
    );
    
    return match;
  }, []);

  // ✅ FILTRAR Y ORDENAR DATOS - Memoizados para evitar recálculos innecesarios
  const filteredDocuments = useMemo(() => {
    return documentsExpirations
      .filter(doc => {
        // ✅ Filtro principal: Excluir vencimientos con más de 1 año vencidos
        if (isMoreThanOneYearExpired(doc.endDate)) {
          return false;
        }
        
        // Aplicar filtros de UI
        return (activeTimeFilters.length === 0 || isItemFiltered(doc, activeTimeFilters)) && 
               (activeDocumentFilters.length === 0 || isItemFiltered(doc, activeDocumentFilters));
      })
      .sort((a, b) => {
        // ✅ Ordenar por fecha de vencimiento usando parseo seguro
        const dateA = parseDateSafely(a.endDate);
        const dateB = parseDateSafely(b.endDate);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return dateA.valueOf() - dateB.valueOf();
      });
  }, [documentsExpirations, activeTimeFilters, activeDocumentFilters, isMoreThanOneYearExpired, parseDateSafely, isItemFiltered]);

  const filteredInspections = useMemo(() => {
    return inspectionsExpirations
      .filter(insp => {
        // ✅ Filtro principal: Excluir vencimientos con más de 1 año vencidos
        if (isMoreThanOneYearExpired(insp.date)) {
          return false;
        }
        
        // Aplicar filtros de UI
        return (activeTimeFilters.length === 0 || isItemFiltered(insp, activeTimeFilters)) && 
               (activeInspectionFilters.length === 0 || isItemFiltered(insp, activeInspectionFilters));
      })
      .sort((a, b) => {
        // ✅ Ordenar por fecha de vencimiento usando parseo seguro
        const dateA = parseDateSafely(a.date);
        const dateB = parseDateSafely(b.date);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return dateA.valueOf() - dateB.valueOf();
      });
  }, [inspectionsExpirations, activeTimeFilters, activeInspectionFilters, isMoreThanOneYearExpired, parseDateSafely, isItemFiltered]);

  // Aplicar estilos de fila basados en el tipo de alerta
  const getDocumentRowStyle = useCallback(params => getAlertStyle(params.data.alertType), []);
  const getInspectionRowStyle = useCallback(params => getAlertStyle(params.data.alertType), []);

  // ✅ COLUMNAS DE DOCUMENTOS (memoizadas)
  const documentsColumnDefs = useMemo(() => [
    {
      headerName: '',
      field: 'person',
      width: 60,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const data = params.data || {};
        const person = data.person;
        const work = data.work;
        
        if (person) {
          return (
            <Link to={`/people/${person.id}`} className="text-primary">
              <FaUserFriends />
            </Link>
          );
        } else if (work && work.id) {
          return (
            <Link to={`/works/${work.id}`} className="text-success">
              <FaBriefcase />
            </Link>
          );
        }
        return '';
      }
    },
    {
      headerName: 'Nombre',
      field: 'displayName1',
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams,
      cellStyle: { fontWeight: 'bold' }
    },
    {
      headerName: 'Apellido',
      field: 'displayName2',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams,
      cellStyle: { fontWeight: 'bold' }
    },
    {
      headerName: 'Documento',
      field: 'document',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    {
      headerName: 'Tipo',
      field: 'name',
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Vencimiento', 
      field: 'endDate', 
      width: 130,
      sort: 'asc',
      comparator: compareExpirationDates,
      valueFormatter: params => formatDateSafely(params.value),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {  
        return formatDateSafely(params.data.endDate);
      },
      floatingFilter: true,
      filterParams: dateFilterParams
    }
  ], [textFilterParams, dateFilterParams, formatDateSafely]);

  // ✅ COLUMNAS DE INSPECCIONES (memoizadas)
  const inspectionsColumnDefs = useMemo(() => [
    {
      headerName: '',
      field: 'person',
      width: 60,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const data = params.data || {};
        const person = data.person;
        const assignedTool = data.assignedTool || {};
        
        if (person) {
          return (
            <Link to={`/people/${person.id}`} className="text-primary">
              <FaUserFriends />
            </Link>
          );
        } else if (assignedTool.person) {
          return (
            <Link to={`/people/${assignedTool.person.id}`} className="text-primary">
              <FaUserFriends />
            </Link>
          );
        } else if (assignedTool.work && assignedTool.work.id) {
          return (
            <Link to={`/works/${assignedTool.work.id}`} className="text-success">
              <FaBriefcase />
            </Link>
          );
        }
        return '';
      }
    },
    {
      headerName: 'Nombre',
      field: 'displayName1',
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams,
      cellStyle: { fontWeight: 'bold' }
    },
    {
      headerName: 'Apellido',
      field: 'displayName2',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams,
      cellStyle: { fontWeight: 'bold' }
    },
    {
      headerName: 'Documento',
      field: 'document',
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    {
      headerName: 'Herramienta',
      field: 'equipment',
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    {
      headerName: 'Tipo',
      field: 'typeDescription',
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Vencimiento', 
      field: 'date', 
      width: 120,
      sort: 'asc',
      comparator: compareExpirationDates,
      valueFormatter: params => formatDateSafely(params.value),
      filter: 'agTextColumnFilter',
      filterValueGetter: params => {  
        return formatDateSafely(params.data.date);
      },
      floatingFilter: true,
      filterParams: dateFilterParams
    }
  ], [textFilterParams, dateFilterParams, formatDateSafely]);

  // Función para cerrar snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <Container fluid className="p-0">
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">Control de Vencimientos</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-2">Cargando datos de vencimientos...</p>
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
              fill
            >
              {/* Pestaña de Documentos */}
              <Tab eventKey="documents" title={
                <div className="d-flex align-items-center">
                  Documentos <Badge bg="primary" pill className="ms-2">{filteredDocuments.length}</Badge>
                </div>
              }>
                <div className="mb-3 d-flex gap-2">
                  <FilterSelect
                    options={documentsFilters}
                    activeFilters={activeDocumentFilters}
                    onFiltersChange={handleDocumentFiltersChange}
                    onOptionsUpdate={setDocumentsFilters}
                    title="Filtrar por tipo de documento"
                    filterKey="alerts-documents"
                  />
                  
                  <FilterSelect
                    options={timeFilterOptions}
                    activeFilters={activeTimeFilters}
                    onFiltersChange={handleTimeFiltersChange}
                    onOptionsUpdate={() => {}} // No necesitamos actualizar las opciones de tiempo
                    title="Filtrar por tiempo"
                    icon={<FaClock />}
                    filterKey="alerts-time"
                  />
                  <div className="ms-auto">
                    <ExportCsvButton gridApi={docsGridApi} moduleName="vencimientos-documentos" />
                  </div>
                </div>

                <TableWrapper
                  columnDefs={documentsColumnDefs}
                  rowData={filteredDocuments}
                  tableType="documentos"
                  onGridReady={(e) => setDocsGridApi(e.api)}
                  getRowStyle={getDocumentRowStyle}
                  customGridOptions={{
                    pagination: true,
                    paginationPageSize: 15,
                    domLayout: 'autoHeight',
                    defaultColDef: {
                      sortable: true,
                      filter: true,
                      resizable: true,
                      floatingFilter: true
                    }
                  }}
                />
              </Tab>
              
              {/* Pestaña de Inspecciones */}
              <Tab eventKey="inspections" title={
                <div className="d-flex align-items-center">
                  Inspecciones <Badge bg="primary" pill className="ms-2">{filteredInspections.length}</Badge>
                </div>
              }>
                <div className="mb-3 d-flex gap-2">
                  <FilterSelect
                    options={inspectionsFilters}
                    activeFilters={activeInspectionFilters}
                    onFiltersChange={handleInspectionFiltersChange}
                    onOptionsUpdate={setInspectionsFilters}
                    title="Filtrar por tipo de inspección"
                    filterKey="alerts-inspections"
                  />
                  
                  <FilterSelect
                    options={timeFilterOptions}
                    activeFilters={activeTimeFilters}
                    onFiltersChange={handleTimeFiltersChange}
                    onOptionsUpdate={() => {}} // No necesitamos actualizar las opciones de tiempo
                    title="Filtrar por tiempo"
                    icon={<FaClock />}
                    filterKey="alerts-time"
                  />
                  <div className="ms-auto">
                    <ExportCsvButton gridApi={inspGridApi} moduleName="vencimientos-inspecciones" />
                  </div>
                </div>

                <TableWrapper
                  columnDefs={inspectionsColumnDefs}
                  rowData={filteredInspections}
                  tableType="inspecciones"
                  onGridReady={(e) => setInspGridApi(e.api)}
                  getRowStyle={getInspectionRowStyle}
                  customGridOptions={{
                    pagination: true,
                    paginationPageSize: 15,
                    domLayout: 'autoHeight',
                    defaultColDef: {
                      sortable: true,
                      filter: true,
                      resizable: true,
                      floatingFilter: true
                    }
                  }}
                />
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default AlertsList;
